import React, { useRef, useState } from "react";
import { Cancel, FileUploadOutlined } from "@mui/icons-material";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Grid, Paper, Slide, Typography, useTheme } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import Dropzone, { ExtendedFile } from "../../common/Dropzone";
import { useForm } from "react-hook-form";
import {
  pdf,
  doc,
  docx,
  jpeg,
  png,
  xls,
  xlsx,
  txt,
  csv,
  gif,
  bmp,
  tiff,
} from "../../../constants/applicationConstants";
import Button from "../../common/Button";
import { removeIVRDocument } from "../../../redux/slice/ivr/ivrDocumentSlice";
import { setSingleAlertObj } from "../../../redux/slice/commonSlice";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { tokens } from "../../../theme";
import CustomTextField from "../../common/CustomTextField";
import InfoPopup from "../../common/InfoPopup";
import { ClaimDocumentInfo, uploadClaimDocument } from "../../../redux/slice/claim/ClaimSlice";

interface DocumentsUploadProps {
  open: boolean;
  onClose: () => void;
  claimId: number;
  onUploadSuccess: () => void;
}

interface Attachment {
  documentName: string;
  content: string;
  fileType: string;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const readFileAsBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const ClaimDocumentPopup: React.FC<DocumentsUploadProps> = ({
  open,
  onClose,
  claimId,
  onUploadSuccess,
}) => {
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    clearErrors,
    trigger,
    watch,
    formState: { errors },
  } = useForm();
  const dispatch = useAppDispatch();
  const documentIdsRef = useRef<number[]>([]);
  const [showSecondaryInsuranceMissing, setShowSecondaryInsuranceMissing] = useState<boolean>();
  const [uploadDoc, setUploadDoc] = useState<ExtendedFile>();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const showNoteField = watch("documentType");


  const onSubmit = async (data: any) => {
    const attachments = getValues("attachmentIVRDocument");
    const attachment = uploadDoc;

    if (attachment) {
      const file = attachment;
      const base64Content = await readFileAsBase64(file);
      const documentObject: ClaimDocumentInfo = {
        claimId: claimId,
        documentName: attachments[0].documentName ? attachments[0].documentName : '',
        content: removeDataUriPrefix(base64Content),
        type: "CLAIM",
        fileType: file.type,
        documentType: "",
      };
      const response = await dispatch(uploadClaimDocument(documentObject));

    }
    onClose();
  };

  const removeDataUriPrefix = (dataUri: string): string => {
    const base64String = dataUri.split(",")[1];
    return base64String || "";
  };

  const handleDropzoneChange = async (uploadedFiles: ExtendedFile[]) => {
    if (uploadedFiles && uploadedFiles.length > 0) {
      clearErrors("attachmentIVRDocument");
      setUploadDoc(uploadedFiles[0]);
      setValue("attachmentIVRDocument", uploadedFiles);
    } else {
      trigger("attachmentIVRDocument");
      return;
    }
    const fileNames = uploadedFiles.map((file) => file.name);
    const attachments: Attachment[] = await Promise.all(
      uploadedFiles.map(async (file) => {
        const base64Content = await readFileAsBase64(file);
        return {
          documentName: file.name,
          content: removeDataUriPrefix(base64Content),
          fileType: file.type,
        };
      })
    );
    setValue("attachmentIVRDocument", attachments);
  };

  const handleDeleteDocument = async (documentId: number) => {
    if (!documentId) {
      setValue("attachmentIVRDocument", []);
      return;
    }
    try {
      const response = await dispatch(removeIVRDocument(documentId.toString()));
      if (response.payload.code === "V3OK") {
        documentIdsRef.current = documentIdsRef.current.filter(
          (id: number) => id !== documentId
        );
        setValue("attachmentIVRDocument", []);
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      dispatch(setSingleAlertObj({ message: "Failed to delete document.", alertType: "error" }));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={onClose}
        aria-describedby="alert-dialog-slide-description"
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <DialogTitle>Upload EOB Documents</DialogTitle>

        <Divider
          orientation="horizontal"
          variant="fullWidth"
          flexItem
          style={{ marginBottom: "-10px" }}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            <Box mt={2}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, mb: 1, width: "100%" }}>
                    <Grid container spacing={2}>
                      <Grid item md xs={12}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: colors.warning[300],
                            fontWeight: 600,
                            textAlign: "center",
                          }}>
                          Upload a document in PDF format
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {showNoteField && (
                  <Grid item xs={12}>
                    <CustomTextField
                      controllerName="content"
                      control={control}
                      label="Notes"
                      multiline
                      rules={{ required: "Notes are required." }}
                    />
                  </Grid>
                )}

                {showSecondaryInsuranceMissing &&
                  <InfoPopup
                    open={showSecondaryInsuranceMissing}
                    content={"Secondary insurance details are not provided for this IVR."}
                    onClose={() => {
                      setShowSecondaryInsuranceMissing(false)
                      setValue("documentType", "")
                    }}
                    severity="info"
                    title={"INFO"}
                  />
                }

                <Grid item xs={12}>
                  <Dropzone
                    name="attachmentIVRDocument"
                    onFileChange={() => { }}
                    onFileChangeTest={handleDropzoneChange}
                    control={control}
                    maxFileCount={1}
                    rules={{ required: "Attachment is required." }}
                    maxFileSize={{ size: 15, unit: "MB" }}
                    onDeleteFile={handleDeleteDocument}
                    allowedFileTypes={{
                      ...pdf,
                      ...doc,
                      ...docx,
                      ...txt,
                      ...csv,
                      ...jpeg,
                      ...png,
                      ...gif,
                      ...xls,
                      ...xlsx,
                      ...bmp,
                      ...tiff,
                    }}
                  />
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    Max size: 15MB
                  </Typography>
                </Grid>

              </Grid>
            </Box>
          </DialogContent>
          <Divider
            orientation="horizontal"
            variant="fullWidth"
            flexItem
            style={{ marginTop: "-6px" }}
          />
          <DialogActions style={{ marginTop: "-6px" }}>
            <Grid container justifyContent="flex-end" spacing={1} p={2}>
              <Grid item xs={12} sm="auto">
                <Button
                  type="submit"
                  startIcon={<FileUploadOutlined />}
                  fullWidth
                >
                  Submit
                </Button>
              </Grid>
              <Grid item xs={12} sm="auto">
                <Button
                  color="error"
                  startIcon={<Cancel />}
                  onClick={onClose}
                  fullWidth
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </form>
      </Dialog>
    </>
  );
};

export default ClaimDocumentPopup;